import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";

/**
 * Shared pieces for the dashboards (owner, 2026-11-04): a date range kept in the address (so a view can be shared),
 * quick picks, number tiles, and small SVG charts. Dates are Central business days.
 */
export interface Range { from: string; to: string }

const day = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
const shift = (iso: string, days: number) => new Date(Date.parse(`${iso}T12:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

export function presets(now = new Date()): { id: string; label: string; range: Range }[] {
  const today = day(now);
  const y = Number(today.slice(0, 4)), m = Number(today.slice(5, 7));
  const firstOf = (yy: number, mm: number) => new Date(Date.UTC(yy, mm - 1, 1)).toISOString().slice(0, 10);
  const lastMonthStart = firstOf(m === 1 ? y - 1 : y, m === 1 ? 12 : m - 1);
  return [
    { id: "today", label: "Today", range: { from: today, to: today } },
    { id: "7d", label: "Last 7 days", range: { from: shift(today, -6), to: today } },
    { id: "month", label: "This month", range: { from: firstOf(y, m), to: today } },
    { id: "lastmonth", label: "Last month", range: { from: lastMonthStart, to: shift(firstOf(y, m), -1) } },
    { id: "ytd", label: "Year to date", range: { from: `${y}-01-01`, to: today } },
    { id: "12m", label: "Last 12 months", range: { from: shift(firstOf(y, m), 0).replace(/^\d{4}/, String(y - 1)), to: today } },
  ];
}

/** The range in the address (?from&to), this month by default. */
export function useRange(): [Range, (r: Range) => void] {
  const [params, setParams] = useSearchParams();
  const fallback = presets().find((p) => p.id === "month")!.range;
  const range = { from: params.get("from") ?? fallback.from, to: params.get("to") ?? fallback.to };
  const set = (r: Range) => { const n = new URLSearchParams(params); n.set("from", r.from); n.set("to", r.to); setParams(n, { replace: true }); };
  return [range, set];
}

export function RangePicker({ range, onChange, children }: { range: Range; onChange: (r: Range) => void; children?: ReactNode }) {
  const list = useMemo(() => presets(), []);
  const active = list.find((p) => p.range.from === range.from && p.range.to === range.to)?.id ?? "custom";
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  return (
    <div className="range-bar" role="group" aria-label="Date range">
      <div className="range-presets">
        {list.map((p) => (
          <button key={p.id} type="button" className={p.id === active ? "on" : ""} aria-pressed={p.id === active}
            onClick={() => { setFrom(p.range.from); setTo(p.range.to); onChange(p.range); }}>{p.label}</button>
        ))}
      </div>
      <form className="range-custom" onSubmit={(e) => { e.preventDefault(); if (from && to && from <= to) onChange({ from, to }); }}>
        <label>From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
        <button type="submit" className="tb-btn" disabled={!from || !to || from > to || (from === range.from && to === range.to)}>Apply</button>
      </form>
      {children}
    </div>
  );
}

export const usd = (n: number, cents = true) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
export const prettyDay = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric", year: "numeric" });

/** A number tile, with the change from the period before when given. */
export function Tile({ label, value, before, money = false, hint, invert = false }: {
  label: string; value: number; before?: number; money?: boolean; hint?: string; invert?: boolean;
}) {
  const text = money ? usd(value, false) : value.toLocaleString("en-US");
  let delta: ReactNode = null;
  if (before != null) {
    const diff = value - before;
    const pct = before ? Math.round((diff / Math.abs(before)) * 100) : null;
    const good = invert ? diff < 0 : diff > 0;
    delta = diff === 0 ? <span className="delta flat">same as before</span>
      : <span className={`delta ${good ? "up" : "down"}`}>{diff > 0 ? "▲" : "▼"} {pct != null ? `${Math.abs(pct)}%` : money ? usd(Math.abs(diff), false) : Math.abs(diff)} vs before</span>;
  }
  return (
    <div className="tile" title={hint}>
      <span className="tile-label">{label}</span>
      <span className="tile-value">{text}</span>
      {delta}
    </div>
  );
}

// ------------------------------------------------------------------ charts (plain SVG)

/** Round a chart's top up to 1, 2, 2.5 or 5 × a power of ten (even, readable gridlines). */
export function niceMax(n: number): number {
  const p = 10 ** Math.floor(Math.log10(n));
  return ([1, 2, 2.5, 5, 10].map((f) => f * p).find((v) => v >= n) ?? 10 * p);
}

export const SERIES = ["var(--series-1)", "var(--series-2)"];

/** Vertical bars, one or two series per group, with a hover tooltip and a legend for two series. */
export function Bars({ groups, series, format, height = 220, label }: {
  groups: { label: string; values: number[] }[]; series: string[]; format: (n: number) => string; height?: number; label: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(1, ...groups.flatMap((g) => g.values)));
  const W = 640, H = height, padL = 52, padB = 26, padT = 8;
  const plotW = W - padL - 8, plotH = H - padB - padT;
  const gw = plotW / Math.max(1, groups.length);
  const bw = Math.max(3, Math.min(28, (gw - 6) / series.length - 2));
  const ticks = [0, 0.5, 1].map((f) => f * max);
  const every = Math.ceil(groups.length / 12);
  return (
    <figure className="chart" aria-label={label}>
      {series.length > 1 && <div className="legend">{series.map((s, i) => <span key={s}><i style={{ background: SERIES[i] }} />{s}</span>)}</div>}
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => {
          const y = padT + plotH - (t / max) * plotH;
          return <g key={t}><line x1={padL} x2={W - 8} y1={y} y2={y} className="grid" /><text x={padL - 6} y={y + 4} className="tick" textAnchor="end">{format(t)}</text></g>;
        })}
        {groups.map((g, i) => {
          const x0 = padL + i * gw + (gw - (bw + 2) * series.length) / 2;
          return (
            <g key={g.label} onMouseEnter={() => setHover(i)}>
              <rect x={padL + i * gw} y={padT} width={gw} height={plotH} className="hit" />
              {g.values.map((v, k) => {
                const h = (v / max) * plotH;
                return <rect key={k} x={x0 + k * (bw + 2)} y={padT + plotH - h} width={bw} height={Math.max(0, h)} rx={Math.min(4, bw / 2)}
                  fill={SERIES[k]} opacity={hover == null || hover === i ? 1 : 0.45} />;
              })}
              {i % every === 0 && <text x={padL + i * gw + gw / 2} y={H - 8} className="tick" textAnchor="middle">{g.label}</text>}
            </g>
          );
        })}
      </svg>
      {hover != null && (
        <div className="chart-tip" style={{ left: `${((padL + (hover + 0.5) * gw) / W) * 100}%` }}>
          <strong>{groups[hover]!.label}</strong>
          {groups[hover]!.values.map((v, k) => <span key={k}><i style={{ background: SERIES[k] }} />{series[k]}: {format(v)}</span>)}
        </div>
      )}
    </figure>
  );
}

/** Horizontal bars for a ranked list (top services, states, open work). */
export function HBars({ rows, format, label }: { rows: { label: string; value: number; note?: string }[]; format: (n: number) => string; label: string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="muted">Nothing in this range.</p>;
  return (
    <ul className="hbars" aria-label={label}>
      {rows.map((r) => (
        <li key={r.label} title={`${r.label}: ${format(r.value)}${r.note ? ` · ${r.note}` : ""}`}>
          <span className="hb-label">{r.label}</span>
          <span className="hb-track"><span className="hb-bar" style={{ width: `${(r.value / max) * 100}%` }} /></span>
          <span className="hb-value">{format(r.value)}{r.note && <small> · {r.note}</small>}</span>
        </li>
      ))}
    </ul>
  );
}

/** A 100% bar split into parts (conversion, approval), labelled with counts and shares. */
export function Split({ parts, label }: { parts: { label: string; value: number; tone: "a" | "b" | "c" }[]; label: string }) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  if (!total) return <p className="muted">Nothing in this range.</p>;
  return (
    <div className="split" aria-label={label}>
      <div className="split-bar">
        {parts.filter((p) => p.value > 0).map((p) => <span key={p.label} className={`seg ${p.tone}`} style={{ flexGrow: p.value }} title={`${p.label}: ${p.value} (${Math.round((p.value / total) * 100)}%)`} />)}
      </div>
      <ul className="split-legend">
        {parts.map((p) => <li key={p.label}><i className={`seg ${p.tone}`} />{p.label} <strong>{p.value.toLocaleString()}</strong> <span className="muted">({Math.round((p.value / total) * 100)}%)</span></li>)}
      </ul>
    </div>
  );
}
